import axios from "axios";
import config from "../../../config";

const baseUrl = config.baseUrl;

/**
 * Formatea bytes a texto legible (KB / MB / GB).
 */
export const formatBytes = (bytes) => {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  if (bytes >= 1024)      return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

/**
 * Descarga un archivo dado su OID.
 * @param {string|number} oid
 * @param {(percent: number, loaded: number, total: number) => void} [onProgress]
 */
export const downloadFile = async (oid, onProgress) => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/repositorio/downldoad_repo/${oid}`,
      {
        responseType: 'blob',
        onDownloadProgress: (event) => {
          if (!onProgress) return;
          const percent = event.total
            ? Math.round((event.loaded / event.total) * 100)
            : -1;
          onProgress(percent, event.loaded, event.total ?? 0);
        },
      }
    );
    return { status: response.status, data: response.data };
  } catch (error) {
    console.error('Error al descargar el archivo:', error);
    throw error;
  }
};

/**
 * Obtiene la URL de visualización de un archivo (apertura directa en browser).
 */
export const getViewFileUrl = (oid) => {
  return `${baseUrl}/api/repositorio/view_repo/${oid}`;
};

/**
 * Crea una carpeta en el repositorio.
 */
export const createFolder = async ({ parentId, folderName, username }) => {
  try {
    const response = await axios.post(`${baseUrl}/api/repositorio/create_folder`, {
      parentId,
      folderName,
      username,
    });
    return response.data;
  } catch (error) {
    console.error("Error al crear carpeta:", error);
    throw error;
  }
};

export const renameFolder = async ({ folderId, newName, username }) => {
  try {
    const response = await axios.post(`${baseUrl}/api/repositorio/rename_folder`, {
      folderId,
      newName,
      username,
    });
    return response.data;
  } catch (error) {
    console.error("Error renombrando carpeta:", error);
    throw error;
  }
};

export const deleteFolder = async ({ folderId, username }) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/repositorio/delete_folder`,
      { folderId, username }
    );
    return response.data;
  } catch (error) {
    console.error("Error al eliminar carpeta:", error);
    throw error;
  }
};

/**
 * Sube uno o varios archivos individuales.
 * @param {FormData} formData
 * @param {string} parentPath
 * @param {(percent: number) => void} [onProgress]
 */
export const uploadFiles = async (formData, parentPath, onProgress) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/repositorio/upload_file?pathFromClient=${encodeURIComponent(parentPath)}`,
      formData,
      {
        onUploadProgress: (event) => {
          if (!onProgress || !event.total) return;
          onProgress(Math.round((event.loaded / event.total) * 100));
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error al subir archivos:", error);
    throw error;
  }
};

export const deleteFile = async ({ fileId, username }) => {
  try {
    const response = await axios.post(`${baseUrl}/api/repositorio/delete_file`, {
      fileId,
      username,
    });
    return response.data;
  } catch (error) {
    console.error("Error al eliminar archivo:", error);
    throw error;
  }
};

/**
 * Descarga varios archivos seleccionados como un único ZIP.
 * @param {number[]} fileIds
 * @param {(percent: number, loaded: number, total: number) => void} [onProgress]
 */
export const downloadZip = async (fileIds, onProgress) => {
  const response = await axios.post(
    `${baseUrl}/api/repositorio/download-zip`,
    { fileIds },
    {
      responseType: 'blob',
      onDownloadProgress: (event) => {
        if (!onProgress) return;
        const percent = event.total
          ? Math.round((event.loaded / event.total) * 100)
          : -1;
        onProgress(percent, event.loaded, event.total ?? 0);
      },
    }
  );
  return response;
};

/**
 * Descarga una carpeta completa como ZIP.
 * @param {string} folderPath
 * @param {(percent: number, loaded: number, total: number) => void} [onProgress]
 */
export const downloadFolderZip = async (folderPath, onProgress) => {
  return axios.post(
    `${baseUrl}/api/repositorio/download-folder-zip`,
    { folderPath },
    {
      responseType: 'blob',
      onDownloadProgress: (event) => {
        if (!onProgress) return;
        const percent = event.total
          ? Math.round((event.loaded / event.total) * 100)
          : -1;
        onProgress(percent, event.loaded, event.total ?? 0);
      },
    }
  );
};

/**
 * Sube una carpeta completa (o un lote de archivos de una carpeta).
 * @param {FormData} formData
 * @param {(percent: number) => void} [onProgress]
 */
export const uploadFolderFile = async (formData, onProgress) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/repositorio/upload_folder`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data; charset=utf-8' },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        onUploadProgress: (event) => {
          if (!onProgress || !event.total) return;
          onProgress(Math.round((event.loaded / event.total) * 100));
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error al subir carpeta:", error);
    throw error;
  }
};

/**
 * Obtiene la lista de documentos del repositorio.
 */
export const getListaDocumentos = async () => {
  try {
    const response = await axios.get(`${baseUrl}/api/repositorio/lista-documentos`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener la lista de documentos:', error);
    throw error;
  }
};

export const updateDocumento = async (documento) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/repositorio/update_documento`,
      documento
    );
    return response.data;
  } catch (error) {
    console.error('Error al actualizar el documento:', error);
    throw error;
  }
};

export const createDocumento = async ({ nombre, link }) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/repositorio/create_documento`,
      { nombre, link }
    );
    return response.data;
  } catch (error) {
    console.error("Error al crear documento:", error);
    throw error;
  }
};

export const deleteDocumento = async (oid) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/repositorio/delete_documento`,
      { oid }
    );
    return response.data;
  } catch (error) {
    console.error("Error al eliminar documento:", error);
    throw error;
  }
};
